import React from 'react';
import {
  Wallet, DollarSign, Clock, AlertTriangle, RefreshCw, Eye,
  ArrowRightLeft, Loader2, CheckCircle, Ban, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function EscrowsTab({
  escrowStats, escrows, escrowStatusFilter, setEscrowStatusFilter, setEscrowsPage,
  fetchEscrows, fetchEscrowStats, formatDate, actionLoading,
  handleViewEscrow, setSelectedEscrow, setShowResolveModal,
  handleForceRelease, handleCancelEscrow,
  escrowsPage, escrowsTotalPages
}) {
  return (
    <div className="space-y-6" data-testid="escrows-tab">
      {escrowStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><Wallet className="h-5 w-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{escrowStats.total}</p>
                <p className="text-sm text-gray-500">Total Escrows</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><DollarSign className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-2xl font-bold text-gray-900">${(escrowStats.amounts?.held || 0).toLocaleString()}</p>
                <p className="text-sm text-gray-500">Funds Held</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center"><Clock className="h-5 w-5 text-amber-600" /></div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{escrowStats.by_status?.funded || 0}</p>
                <p className="text-sm text-gray-500">Active/Funded</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-red-600" /></div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{escrowStats.by_status?.disputed || 0}</p>
                <p className="text-sm text-gray-500">Disputed</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex flex-wrap gap-4 justify-between items-center">
          <h3 className="font-semibold text-gray-900">Escrow Transactions</h3>
          <div className="flex items-center gap-3">
            <select
              value={escrowStatusFilter}
              onChange={(e) => { setEscrowStatusFilter(e.target.value); setEscrowsPage(1); }}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="funded">Funded</option>
              <option value="partially_released">Partially Released</option>
              <option value="released">Released</option>
              <option value="disputed">Disputed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <Button onClick={() => { fetchEscrows(); fetchEscrowStats(); }} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contractor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Worker</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {escrows.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No escrows found</td></tr>
              ) : escrows.map((escrow) => (
                <tr key={escrow.id} className="hover:bg-gray-50" data-testid={`escrow-${escrow.id}`}>
                  <td className="px-4 py-3"><p className="font-medium text-gray-900 truncate max-w-[150px]">{escrow.job_title}</p></td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-900">{escrow.contractor_name}</p>
                    <p className="text-xs text-gray-500">{escrow.contractor_email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-900">{escrow.worker_name}</p>
                    <p className="text-xs text-gray-500">{escrow.worker_email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">${(escrow.total_amount || 0).toLocaleString()}</p>
                    {escrow.released_amount > 0 && (
                      <p className="text-xs text-green-600">Released: ${escrow.released_amount.toLocaleString()}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={
                      escrow.status === 'funded' ? 'bg-blue-100 text-blue-800' :
                      escrow.status === 'released' ? 'bg-green-100 text-green-800' :
                      escrow.status === 'disputed' ? 'bg-red-100 text-red-800' :
                      escrow.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                      escrow.status === 'partially_released' ? 'bg-amber-100 text-amber-800' :
                      'bg-yellow-100 text-yellow-800'
                    }>{escrow.status?.replace('_', ' ')}</Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{formatDate(escrow.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleViewEscrow(escrow)} data-testid={`view-escrow-${escrow.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {escrow.status === 'disputed' && (
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedEscrow(escrow); setShowResolveModal(true); }} className="text-orange-600 hover:text-orange-700" data-testid={`resolve-escrow-${escrow.id}`}>
                          <ArrowRightLeft className="h-4 w-4" />
                        </Button>
                      )}
                      {['funded', 'partially_released'].includes(escrow.status) && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handleForceRelease(escrow.id)} disabled={actionLoading === escrow.id} className="text-green-600 hover:text-green-700" title="Force Release to Worker">
                            {actionLoading === escrow.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleCancelEscrow(escrow.id)} disabled={actionLoading === escrow.id} className="text-red-600 hover:text-red-700" title="Cancel & Refund">
                            <Ban className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {escrowsTotalPages > 1 && (
          <div className="p-4 border-t border-gray-200 flex justify-between items-center">
            <p className="text-sm text-gray-500">Page {escrowsPage} of {escrowsTotalPages}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEscrowsPage(p => Math.max(1, p - 1))} disabled={escrowsPage === 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEscrowsPage(p => Math.min(escrowsTotalPages, p + 1))} disabled={escrowsPage === escrowsTotalPages}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
